import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { usePermissions } from "@/hooks/usePermissions";
import IssueDetail from "./IssueDetail";
import Typography from "@/Utility/Typography";
import { getWorkItemsListPathForPermissions } from "./issuesTabPermissions";
import { buildWorkItemDetailPath } from "@/utils/workItemDetailUrl";
import { fetchProjectsData } from "@/Redux/Thunks/projectManagementThunk";
import { selectProjectsListForDropdown } from "@/Redux/Slices/projectsManagementSlice";

/**
 * Shareable work item page — `/issues/work-items/:workItemId?projectId=…`
 * Uses work-items permissions (not `/projects/:id` workspace).
 */
export default function WorkItemSharedDetailPage() {
  const dispatch = useDispatch();
  const { workItemId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const projectsDropdown = useSelector(selectProjectsListForDropdown);
  const projectId = searchParams.get("projectId")?.trim();
  const issuesListPath = getWorkItemsListPathForPermissions(can);
  const [projectDisplayName, setProjectDisplayName] = useState("");

  useEffect(() => {
    if (!projectsDropdown?.list?.length) {
      dispatch(fetchProjectsData({ getAll: true }));
    }
  }, [dispatch, projectsDropdown?.list?.length]);

  if (!workItemId || !projectId) {
    return (
      <div className="px-1 py-6 text-center">
        <Typography variant="text1" className="text-text-secondary">
          Invalid work item link.
        </Typography>
        <Link
          to={issuesListPath}
          className="mt-2 inline-block text-sm text-text-link hover:underline"
        >
          Go to work items
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Typography variant="h3" className="mt-3 text-text-primary">
        {projectDisplayName || "Project"}
      </Typography>
      <IssueDetail
        projectSlug={projectId}
        issueId={workItemId}
        presentation="page"
        onProjectDisplayNameChange={setProjectDisplayName}
        onOpenRelatedWorkItem={(relatedId) => {
          if (!relatedId) return;
          navigate(
            buildWorkItemDetailPath({
              projectSlug: projectId,
              workItemId: String(relatedId),
            })
          );
        }}
        onProjectIdChange={(newProjectId) => {
          if (!newProjectId || !workItemId) return;
          navigate(
            buildWorkItemDetailPath({
              projectSlug: String(newProjectId),
              workItemId,
            }),
            { replace: true }
          );
        }}
      />
    </div>
  );
}
